import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import style from "./UploadDocumentModal.module.scss";
import { File } from "../../../../../services";
import Loading from "../../../../shared/Loading/Loading";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";

const UploadDocumentModal = ({ isOpen, onClose, order, refreshDocuments }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [docName, setDocName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isUploading) return;
    setDocName("");
    setUploadFile(null);
    setIsDragging(false);
    onClose();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setUploadFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setUploadFile(file);
  };

  const handleSubmit = () => {
    if (!docName.trim()) {
      toast.error(t("orderInfo.documentsTab.docNameRequired"));
      return;
    }
    if (!uploadFile) {
      toast.error(t("orderInfo.documentsTab.fileRequired"));
      return;
    }
    if (!order?.id) return;

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("FileName", uploadFile.name);
    formData.append("orderId", order.id);
    formData.append("name", docName.trim());
    formData.append("Name", docName.trim());
    formData.append("type", 5);

    setIsUploading(true);
    File.addOrderDoc(formData)
      .then(() => {
        toast.success(t("orderInfo.documentsTab.uploadSuccess"));
        refreshDocuments && refreshDocuments();
        handleClose();
      })
      .catch(() => toast.error(t("orderInfo.documentsTab.uploadError")))
      .finally(() => setIsUploading(false));
  };

  return ReactDOM.createPortal(
    <div className={style.overlay} onClick={handleClose} data-portal-modal="true">
      <div className={style.modal} onClick={(e) => e.stopPropagation()}>
        <div className={style.topbar}>
          <span className={style.title}>{t("orderInfo.documentsTab.modalTitle")}</span>
          <button className={style.close_btn} onClick={handleClose}>
            <CloseIconNew />
          </button>
        </div>

        <div className={style.section}>
          <div className={style.section_header}>
            <span className={style.section_title}>{t("orderInfo.documentsTab.modalNameSection")}</span>
          </div>
          <div className={style.input_wrap}>
            <label className={style.input_label}>
              {t("orderInfo.documentsTab.docNameLabel")}
              <span className={style.required}>*</span>
            </label>
            <input
              className={cn(style.input, { [style.input_error]: !docName.trim() && docName !== "" })}
              type="text"
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder={t("orderInfo.documentsTab.docNamePlaceholder")}
            />
          </div>
        </div>

        <div className={style.section}>
          <div className={style.section_header}>
            <span className={style.section_title}>{t("orderInfo.documentsTab.modalFileSection")}</span>
          </div>
          <div
            className={cn(style.dropzone, {
              [style.dropzone_active]: isDragging,
              [style.dropzone_has_file]: !!uploadFile,
            })}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={style.dropzone_icon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#6d7885">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
              </svg>
            </div>
            {uploadFile ? (
              <span className={style.dropzone_filename}>{uploadFile.name}</span>
            ) : (
              <div className={style.dropzone_text}>
                <span className={style.dropzone_text_main}>{t("orderInfo.documentsTab.dropzoneClick")}</span>
                <span className={style.dropzone_text_sub}>{t("orderInfo.documentsTab.dropzoneOr")}</span>
                <span className={style.dropzone_text_hint}>{t("orderInfoExpeditor.documentsTab.fileHint")}</span>
              </div>
            )}
            <input ref={fileInputRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={handleFileChange} />
          </div>
        </div>

        <div className={style.footer}>
          <button className={cn(style.btn, style.btn_cancel)} onClick={handleClose} disabled={isUploading}>
            {t("common.cancel")}
          </button>
          <button className={cn(style.btn, style.btn_submit)} onClick={handleSubmit} disabled={isUploading}>
            {isUploading ? <Loading inBlock marginBottom={0} /> : t("common.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UploadDocumentModal;
